import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, PriceTagIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import SelectInput from '@repo/ui/select-input'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { createPaymentRecord } from '../../../../api/enrollments/create-payment-record'
import { getPaymentRecordsStatusesStatistics } from '../../../../api/enrollments/get-payment-records-statistics'
import { updatePaymentRecord } from '../../../../api/enrollments/update-payment-record'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import {
  PaymentMeansEnum,
  paymentMeansEnumMapping
} from '../../../../lib/types/enums/payment-record/payment-means.enum'
import { paymentTypeEnumMapping } from '../../../../lib/types/enums/payment-record/payment-type.enum'
import {
  StatusEnum,
  statusEnumMapping
} from '../../../../lib/types/enums/payment-record/status.enum'
import {
  enrollmentPaymentRecordTableFields,
  EnrollmentPaymentRecordTableType
} from '../../../../lib/types/fields/payment-records/payment-record'
import { PaymentRecordInput } from '../../../../lib/types/models/payment-record/payment-record-input'
import { UpdateInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../../elements/dynamic-table'
import NestedDataTable from '../../../elements/nested-table'
import EnrollmentPaymentRecordForm from './enrollment-payment-record-form'

type Props = {
  enrollmentId: number
}

const EnrollmentPaymentRecord = ({ enrollmentId }: Props) => {
  const tableRef = useRef<DynamicTableRef<EnrollmentPaymentRecordTableType>>(null)
  const queryClient = useQueryClient()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    EnrollmentPaymentRecordTableType
  >(['create', 'edit'])

  useObserver({
    key: 'enrollment-payment-record',
    update: async () => {
      await queryClient.invalidateQueries({ queryKey: ['paymentRecordsStatistics', enrollmentId] })
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: createPaymentRecordMutate } = useMutation({
    mutationFn: createPaymentRecord,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentRecordsStatistics', enrollmentId] })
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Paiement créé avec succès')
    }
  })

  const { mutateAsync: updatePaymentRecordMutate } = useMutation({
    mutationFn: async (data: UpdateInput<PaymentRecordInput>) => {
      return updatePaymentRecord(data, enrollmentId)
    },
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentRecordsStatistics', enrollmentId] })
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Paiement modifié avec succès')
    }
  })

  const columns: Column<EnrollmentPaymentRecordTableType>[] = [
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => paymentTypeEnumMapping[row.original.type].label
    },
    {
      accessorKey: 'amount',
      header: 'Montant',
      enableSorting: true,
      cell: ({ row }) => NumberFormatter.formatCurrency(row.original.amount)
    },
    {
      accessorKey: 'means',
      header: 'Moyen de paiement',
      enableSorting: true,
      cell: ({ row }) => paymentMeansEnumMapping[row.original.means]
    },
    {
      accessorKey: 'dateDue',
      header: "Date d'échéance",
      enableSorting: true,
      cell: ({ row }) => DateFormatter.DDMMYYYY(row.original.dateDue)
    },
    {
      accessorKey: 'dateEffective',
      header: 'Date réalisée',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.dateEffective ? DateFormatter.DDMMYYYY(row.original.dateEffective) : '-'
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.means === PaymentMeansEnum.CHEQUE &&
        row.original.status === StatusEnum.PENDING ? (
          <SelectInput
            mode="badge"
            label={statusEnumMapping[row.original.status].label}
            variantColor={statusEnumMapping[row.original.status].variant}
            options={Object.values(StatusEnum).map((status) => ({
              value: status,
              label: statusEnumMapping[status].label
            }))}
            onChange={async (status) => {
              await updatePaymentRecordMutate({ id: row.original.id, status })
            }}
            selectedOption={{
              label: statusEnumMapping[row.original.status].label,
              value: row.original.status
            }}
          />
        ) : (
          <Badge
            text={statusEnumMapping[row.original.status].label}
            variant={statusEnumMapping[row.original.status].variant}
          />
        )
    }
  ]

  return (
    <div id="enrollment-payments">
      <NestedDataTable<EnrollmentPaymentRecordTableType, null>
        ref={tableRef}
        overflow
        variant="action"
        title={'Enregistrements des paiements'}
        widget={<EnrollmentPaymentRecord.PaymentSummary enrollmentId={enrollmentId} />}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un paiement',
            fn: () => toggleModal('create')
          }
        ]}
        permissions={(row) => ({
          delete: false,
          edit: row.status === StatusEnum.PENDING && row.means === PaymentMeansEnum.CHEQUE
        })}
        columns={columns}
        onEditAction={(payment) => toggleModal('edit', payment)}
        extraParams={{ fields: enrollmentPaymentRecordTableFields }}
        endpoint={API.enrollments.getPaymentRecords.url.replace(':id', enrollmentId.toString())}
      />
      <Modal
        isOpen={modalState.create}
        title={'Ajouter un paiement'}
        icon={PriceTagIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <EnrollmentPaymentRecordForm
          mode="create"
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (values) => createPaymentRecordMutate({ id: enrollmentId, ...values })
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un paiement'}
          icon={PriceTagIcon}
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <EnrollmentPaymentRecordForm
            mode="edit"
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: (values) => updatePaymentRecordMutate({ id: selectedData.id, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default EnrollmentPaymentRecord

type PaymentSummaryProps = {
  enrollmentId: number
  mode?: 'client' | 'default'
}

EnrollmentPaymentRecord.PaymentSummary = ({
  mode = 'default',
  enrollmentId
}: PaymentSummaryProps) => {
  const { data: paymentRecordsStatistics, refetch: refetchPaymentRecordsStatistics } = useQuery({
    queryKey: ['paymentRecordsStatistics', enrollmentId],
    queryFn: () => getPaymentRecordsStatusesStatistics({ enrollmentId })
  })

  useObserver({
    key: 'enrollment-payment-record',
    update: () => {
      refetchPaymentRecordsStatistics()
    }
  })

  // the backend total already takes off the refunds that lower the price, so the three amounts
  // add up: total - payé = restant
  return (
    <div className="flex items-center gap-2 mobile:flex-col mobile:items-start mobile:gap-1">
      <Badge
        text={`Montant total ${paymentRecordsStatistics?.totalAmount ?? 0} €`}
        variant="warning"
      />
      <Badge text={`Montant payé ${paymentRecordsStatistics?.paidAmount ?? 0} €`} variant="grey" />
      <Badge
        text={`${mode === 'client' ? 'Solde à payer' : 'Montant restant'} ${paymentRecordsStatistics?.pendingAmount ?? 0} €`}
        variant="blue"
      />
      {mode === 'default' && !!paymentRecordsStatistics?.overpaidAmount && (
        <Badge text={`Trop-perçu ${paymentRecordsStatistics.overpaidAmount} €`} variant="orange" />
      )}
    </div>
  )
}
